import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

interface Section {
  heading: string;
  content: string | string[];
}

const ImpressumPage: React.FC = () => {
  const { currentLanguage } = useLanguage();

  const isAr = currentLanguage === "ar";

  const sections: Section[] = isAr
    ? [
        {
          heading: "معلومات وفق § 5 TMG",
          content: [
            "TOP Buchhaltungsservice UG",
            "Stuttgartstraße 15-17",
            "44143 Dortmund",
            "Deutschland",
            "www.top-bhs.de",
          ],
        },
        {
          heading: "التمثيل",
          content: "المدير التنفيذي المفوض: Hasan Al Khdr",
        },
        {
          heading: "التواصل",
          content: [
            "الهاتف: 0157 / 55659651",
            "البريد الإلكتروني: hk@top-bhs.de",
          ],
        },
        {
          heading: "التسجيل التجاري",
          content: [
            "رقم السجل التجاري: HRB 36764",
            "محكمة التسجيل: Amtsgericht Dortmund",
          ],
        },
        {
          heading: "رقم ضريبة القيمة المضافة",
          content: "رقم التعريف الضريبي وفق § 27a UStG: DE 451330947",
        },
        {
          heading: "المسؤول عن المحتوى",
          content: "Hasan Al Khdr (العنوان كما هو مذكور أعلاه)",
        },
        {
          heading: "حل النزاعات",
          content:
            "المفوضية الأوروبية توفر منصة لتسوية النزاعات عبر الإنترنت (OS): https://ec.europa.eu/consumers/odr/. لسنا مُلزمين أو على استعداد للمشاركة في إجراءات تسوية النزاعات أمام هيئة تحكيم المستهلك.",
        },
      ]
    : currentLanguage === "en"
      ? [
          {
            heading: "Information pursuant to § 5 TMG",
            content: [
              "TOP Buchhaltungsservice UG",
              "Stuttgartstraße 15-17",
              "44143 Dortmund",
              "Germany",
              "www.top-bhs.de",
            ],
          },
          {
            heading: "Represented by",
            content: "Authorised Managing Director: Hasan Al Khdr",
          },
          {
            heading: "Contact",
            content: ["Phone: 0157 / 55659651", "Email: hk@top-bhs.de"],
          },
          {
            heading: "Commercial Register",
            content: [
              "Registration number: HRB 36764",
              "Register court: Amtsgericht Dortmund",
            ],
          },
          {
            heading: "VAT ID",
            content:
              "VAT identification number pursuant to § 27a UStG: DE 451330947",
          },
          {
            heading: "Person responsible for content",
            content: "Hasan Al Khdr (address as above)",
          },
          {
            heading: "Dispute Resolution",
            content:
              "The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr/. We are not obliged or willing to participate in dispute resolution proceedings before a consumer arbitration board.",
          },
        ]
      : [
          {
            heading: "Angaben gemäß § 5 TMG",
            content: [
              "TOP Buchhaltungsservice UG",
              "Stuttgartstraße 15-17",
              "44143 Dortmund",
              "Deutschland",
              "www.top-bhs.de",
            ],
          },
          {
            heading: "Vertreten durch",
            content: "Vertretungsberechtigter Geschäftsführer: Hasan Al Khdr",
          },
          {
            heading: "Kontakt",
            content: ["Telefon: 0157 / 55659651", "E-Mail: hk@top-bhs.de"],
          },
          {
            heading: "Handelsregister",
            content: [
              "Handelsregister: HRB 36764",
              "Registergericht: Amtsgericht Dortmund",
            ],
          },
          {
            heading: "Umsatzsteuer-ID",
            content:
              "Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE 451330947",
          },
          {
            heading: "Inhaltlich verantwortlich",
            content: "Hasan Al Khdr (Anschrift wie oben)",
          },
          {
            heading: "Streitschlichtung",
            content:
              "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/. Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
          },
        ];

  const title =
    currentLanguage === "ar"
      ? "البيانات القانونية"
      : currentLanguage === "en"
        ? "Imprint"
        : "Impressum";

  return (
    <Container maxWidth="md" sx={{ py: 10, dir: isAr ? "rtl" : "ltr" }}>
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          letterSpacing: 3,
          display: "block",
          mb: 2,
        }}
      >
        {currentLanguage === "ar"
          ? "قانوني"
          : currentLanguage === "en"
            ? "Legal"
            : "Rechtliches"}
      </Typography>
      <Typography variant="h2" sx={{ mb: 6 }}>
        {title}
      </Typography>

      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {section.heading}
          </Typography>
          {Array.isArray(section.content) ? (
            section.content.map((line, i) => (
              <Typography key={i} variant="body1" sx={{ mb: 0.5 }}>
                {line}
              </Typography>
            ))
          ) : (
            <Typography variant="body1">{section.content}</Typography>
          )}
          {idx < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}
    </Container>
  );
};

export default ImpressumPage;
